import { mergeDebuggerUpdate, shouldPinPlaybook, observePlaybookSession } from './playbook/view-lifecycle';
import React, {useCallback, useState, useContext, useRef} from "react";
import {useAppContext, useAppInfo, useQuery} from "../../helpers/hooks/common-hook";
import FormLoader from "../../components/form-loader/form-loader";
import { useEffect } from "react";
import SectionHeading from "./section-heading";
import PreviewWorkspace from "./preview-workspace";
import ChatPreview from "./chat-preview";

import {AiOutlinePlus} from "react-icons/ai";
import { MdOutlineBugReport, MdFormatListBulleted, MdVisibility, MdVisibilityOff } from "react-icons/md";
import WorkflowDebugger from "./workflow";
import {journeyGet} from "../../services/model/journey-service";
import {getRuntimeDebugSession} from "../../services/runtime/runtime-service";
import {createUniqueId} from "../../helpers/utils/object-util";
import { subscribeRuntimeDebugger } from "../../helpers/utils/real-time-api";
import ConversationData from "./conversation-data";
import Toolbox from "../action-journey-studio/toolbox";
import WorkflowCanvas from "../../components/workflow/WorkflowCanvas";
import {ReactFlowProvider} from "reactflow";
import {WorkflowCanvasContext} from "../../contexts/WorkflowCanvasContextProvider";
import {CgSpinner} from "react-icons/cg";
import AgentMonitor from "./agent-monitor";
import TraceView from "./agent-monitor/TraceView";
import {getPromptConfigInstructions, getPromptConfigTools} from "../../services/prompt-ai/index";
import {useMockAgentEvents} from "./agent-monitor/mock-data";

// "workflow" = show WorkflowDebugger, "agent" = show AgentMonitor
// lastEventType tracks which event arrived most recently to decide the active view
function resolveActiveView(lastEventType, hasJourneySession, hasPromptConfig, pinnedPlaybook) {
  if (pinnedPlaybook && hasJourneySession) return "workflow";
  if (lastEventType === "agent" && hasPromptConfig) return "agent";
  if (lastEventType === "workflow" && hasJourneySession) return "workflow";
  // Fallback: if no event yet, show agent view if config available
  if (hasPromptConfig) return "agent";
  if (hasJourneySession) return "workflow";
  return null;
}

function Index() {
  const query = useQuery();
  const v2 = query.get('v2');

  const { appId } = useAppInfo();
  const workflowCanvasContext = useContext(WorkflowCanvasContext);
  // TODO
  const [sessionId, setSessionId] = useState();
  const [debuggerData, setDebuggerData] = useState({
    agentConfig: null,
    journeySession: null
  });
  const [currentJourneySession, setCurrentJourneySession] = useState();
  const [currentJourneyConfig, setCurrentJourneyConfig] = useState();
  const [showTrace, setShowTrace] = useState(false);
  const [showAgent, setShowAgent] = useState(false);
  const [chatActionsContainer, setChatActionsContainer] = useState(null);

  // Tracks which event type arrived last: "agent" or "workflow"
  const [lastEventType, setLastEventType] = useState(null);

  // Agent Monitor state
  const [promptConfig, setPromptConfig] = useState(null);
  const [promptTools, setPromptTools] = useState(null);
  const [agentEvents, setAgentEvents] = useState([]);
  const agentEventsRef = useRef([]);

  // Mock mode for local development (no real-time events locally)
  const DEV_MOCK_ENABLED = false;
  console.log("DEV_MOCK_ENABLED", DEV_MOCK_ENABLED)
  const mock = useMockAgentEvents(DEV_MOCK_ENABLED);

  const [dataLoadingState, setDataLoading] = useState({
    processing: false,
    success: false,
    failed: false,
    message: null,
  });

  const updateDataLoadingState = (processing, success, failed, message) => {
    setDataLoading(Object.assign({}, { processing, success, failed, message }));
  };

  // Load PromptConfig data on mount
  useEffect(() => {
    async function loadPromptConfig() {
      try {
        const [instructions, tools] = await Promise.all([
          getPromptConfigInstructions(appId),
          getPromptConfigTools(appId),
        ]);
        if (instructions) {
          setPromptConfig(instructions);
        }
        if (tools) {
          setPromptTools(Array.isArray(tools) ? tools : tools?.data || []);
        }
      } catch (error) {
        console.error("Failed to load prompt config", error);
      }
    }
    loadPromptConfig();
  }, [appId]);

  useEffect(() => {
    workflowCanvasContext.setNodes([]);
    workflowCanvasContext.setEdges([]);
    workflowCanvasContext.setIsPreview(true)
    workflowCanvasContext.setConnectingSource(null)
    workflowCanvasContext.setConnectingTarget(null)
    if (!sessionId) {
      setSessionId(createUniqueId());
    }
  }, []);

  const fetchSessionData = async () => {
    try {
      const data = await getRuntimeDebugSession(appId, sessionId);
      if (data && data.response) {
        setDebuggerData(data.response)
      }
    } catch (error) {
      console.error("fetchSessionData failed", error);
    }
  }

  useEffect( () => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  const [initReconnect, setInitReconnect] = useState({});

  const initSubscription = () => {
    const subscription = subscribeRuntimeDebugger(
        debuggerData.defaultAccessKey,
        appId,
        sessionId,
        async ({ data }) => {
          const newDebuggerData = JSON.parse(data)
          // console.log('ON DATA', newDebuggerData)
          await new Promise(resolve => setTimeout(resolve, 0.2));

          // Accumulate agent events for the trace view
          if (newDebuggerData.agentEvent) {
            const newEvent = newDebuggerData.agentEvent;
            // Use workflowRunning flag to decide which view to show
            if (newEvent.workflowRunning) {
              setLastEventType("workflow");
            } else {
              setLastEventType("agent");
            }
            const alreadyExists = agentEventsRef.current.some(e => e.eventId === newEvent.eventId);
            if (!alreadyExists) {
              agentEventsRef.current = [...agentEventsRef.current, newEvent];
              setAgentEvents(agentEventsRef.current);
            }
          }

          if (newDebuggerData.journeySession) {
            setLastEventType("workflow");
          }

          setDebuggerData(prev => mergeDebuggerUpdate(prev, newDebuggerData, Array.isArray(promptTools) ? promptTools : []))
         // setCurrentJourneySession
        },
        (error) => {
          if (error.error.errors[0].message === "Connection closed") {
            console.error("CONN FAILED", error);
            setInitReconnect({});
          }
        }
    );
    console.log("subscription", subscription);
    return () => {
      console.log("unsubscribe......");
      subscription.unsubscribe();
    };
  };

  useEffect(() => {
    console.log("initReconnect");
    if (debuggerData) {
      return initSubscription();
    }
  }, [initReconnect]);

  useEffect( () => {
    if (debuggerData) {
      setCurrentJourneySession(debuggerData.journeySession || undefined);
      return initSubscription();
    }
  }, [debuggerData]);

  useEffect( () => {
    if (currentJourneySession) {
      const config = debuggerData?.agentConfig?.journeys?.find(config => config.journeyId === currentJourneySession.journeyId);
      setCurrentJourneyConfig(config);

      // Sync to workflow canvas context immediately when session updates
      workflowCanvasContext.setCurrentJourneySession(currentJourneySession);
      if (config) {
        workflowCanvasContext.setCurrentJourneyConfig(config);
      }
    } else {
      setCurrentJourneyConfig(undefined);
    }
  }, [currentJourneySession, debuggerData?.agentConfig]);

  // Use mock data in dev mode, real data otherwise
  const effectivePromptConfig = DEV_MOCK_ENABLED ? mock.promptConfig : promptConfig;
  const effectiveTools = DEV_MOCK_ENABLED ? mock.tools : promptTools;
  const effectiveAgentEvents = DEV_MOCK_ENABLED ? mock.events : agentEvents;

  // Derive lastEventType from mock events when in mock mode
  const effectiveLastEventType = (() => {
    if (DEV_MOCK_ENABLED && effectiveAgentEvents.length > 0) {
      const latest = effectiveAgentEvents[effectiveAgentEvents.length - 1];
      return latest.workflowRunning ? "workflow" : "agent";
    }
    return lastEventType;
  })();

  const pinnedPlaybook = shouldPinPlaybook(currentJourneyConfig, currentJourneySession, debuggerData.playbookExited);

  // Some runtimes do not send a final journey event after clearing the session.
  // Confirm exits from the saved session. First observe this journey in storage
  // so an older empty snapshot during startup cannot unpin a running playbook.
  useEffect(() => {
    if (!pinnedPlaybook || !sessionId) return undefined;
    let cancelled = false;
    let timer;
    let observedActive = false;
    const journeyId = currentJourneySession.journeyId;
    const checkSession = async () => {
      try {
        const result = await getRuntimeDebugSession(appId, sessionId);
        if (cancelled) return;
        const observed = observePlaybookSession(result?.response, journeyId, observedActive);
        observedActive = observed.observedActive;
        if (observed.exited) {
          setDebuggerData(previous => previous.journeySession?.journeyId === journeyId
            ? { ...previous, playbookExited: true } : previous);
          setLastEventType('agent');
          return;
        }
      } catch (_) { /* Keep the active playbook visible during temporary failures. */ }
      if (!cancelled) timer = setTimeout(checkSession, 2500);
    };
    checkSession();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [appId, sessionId, pinnedPlaybook, currentJourneySession?.journeyId]);

  const activeView = resolveActiveView(
    effectiveLastEventType,
    !!(currentJourneySession && currentJourneyConfig),
    !!(effectivePromptConfig && effectiveTools),
    pinnedPlaybook
  );

  // Workflow execution remains visible independently of the Agent View toggle.
  const showExecutionPane = activeView === "workflow" || showAgent;

  return (

      <PreviewWorkspace appId={appId} sessionId={sessionId}>
        <div className={`flex h-full ${showExecutionPane && showTrace ? 'min-w-[900px]' : showExecutionPane || showTrace ? 'min-w-[640px]' : 'min-w-[380px]'}`}>
          <div className="relative flex h-[calc(100vh-106px)] min-h-[400px] min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex min-h-[64px] shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white"><MdOutlineBugReport size={21} aria-hidden="true" /></span>
                <div>
                  <h1 className="text-sm font-semibold text-gray-900">Debugger</h1>
                  <p className="mt-0.5 text-xs text-gray-500">Follow execution and inspect changes</p>
                </div>
              </div>
              <div role="group" aria-label="Debugger controls" className="flex max-w-full shrink-0 flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap">
                {[{ label: 'Agent View', visible: showAgent, toggle: setShowAgent, id: 'debugger-agent-view' },
                  { label: 'Trace View', visible: showTrace, toggle: setShowTrace, id: 'debugger-trace-view' }].map(({ label, visible, toggle, id }) => (
                  <button key={id} type="button" aria-label={`${visible ? 'Hide' : 'Show'} ${label}`} title={`${visible ? 'Hide' : 'Show'} ${label}`} aria-expanded={visible} aria-controls={id} onClick={() => toggle(value => !value)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${visible
                      ? 'border-gray-300 bg-gray-100 text-gray-900 hover:bg-gray-200'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                    {visible ? <MdVisibilityOff size={16} aria-hidden="true" /> : <MdVisibility size={16} aria-hidden="true" />}
                    {label}
                  </button>
                ))}
              <div ref={setChatActionsContainer} className="flex shrink-0 items-center border-l border-gray-200 pl-2" />
              </div>
            </div>
            <div className="flex min-h-0 flex-1 overflow-hidden">

              {/* Keep the agent canvas mounted while hidden so its view state is preserved. */}
              <div id="debugger-agent-view" className={`${showExecutionPane ? 'flex' : 'hidden'} min-w-0 flex-1 overflow-hidden`}>
              {activeView === "workflow" ?
                  <WorkflowDebugger
                      currentJourneySession={currentJourneySession}
                      currentJourneyConfig={currentJourneyConfig}
                  />
                  : activeView === "agent" ?
                  <AgentMonitor
                      promptConfig={effectivePromptConfig}
                      tools={effectiveTools}
                      agentEvents={effectiveAgentEvents}
                      onHide={() => setShowAgent(false)}
                  />
                  :
                  <div className="flex flex-grow items-center justify-center bg-gray-50/60 p-8">
                    <div className="max-w-xs text-center">
                      <MdOutlineBugReport size={28} aria-hidden="true" className="mx-auto mb-4 text-gray-400" />
                      <h2 className="text-sm font-semibold text-gray-900">Ready to debug</h2>
                      <p className="mt-2 text-sm leading-relaxed text-gray-500">Send a message in Preview to follow your agent’s steps here.</p>
                    </div>
                  </div>
              }

              </div>

              {/* Keep trace state mounted while collapsed; events continue accumulating. */}
              <div id="debugger-trace-view" className={`${showTrace ? 'flex' : 'hidden'} h-full ${showExecutionPane ? 'w-[300px] shrink-0 2xl:w-[360px]' : 'min-w-0 flex-1'} flex-col border-l border-gray-200 bg-gray-50/60`}>
                <SectionHeading title="Trace View" subtitle={`${effectiveAgentEvents.length} ${effectiveAgentEvents.length === 1 ? 'event' : 'events'} captured`} icon={MdFormatListBulleted}>
                  <button
                      type="button"
                      aria-label="Hide Trace View"
                      aria-expanded={true}
                      aria-controls="debugger-trace-view"
                      onClick={() => setShowTrace(false)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <MdVisibilityOff size={15} aria-hidden="true" /> Hide
                  </button>
                </SectionHeading>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <TraceView events={effectiveAgentEvents} />
                </div>
              </div>

              <ChatPreview actionsContainer={chatActionsContainer} expanded={!showExecutionPane && !showTrace} sessionId={sessionId} currentJourneySession={currentJourneySession}/>
            </div>
          </div>
        </div>
      </PreviewWorkspace>
  );
}

export default Index;
